import { Routes, Route, Navigate } from "react-router-dom";
import AuthLayout from "layouts/AuthLayout";
import PageLayout from "layouts/PageLayout";
import RequireAuth from "app-router/RequireAuth";
import { useAuth } from "context/AuthContext";
import { ROUTE_PERMISSIONS, getDefaultAuthorizedRoute } from "helpers/auth/rbac";
import Login from "pages/login";
import Dashboard from "pages/dashboard";
import Page404 from "pages/404";
import AdminUsers from "pages/admin-users";
import Users from "pages/users";
import UserDetails from "pages/user-details";
import Scans from "pages/scans";
import Brands from "pages/brands";
import { ROUTES } from "./routes";
import CategoriesPage from "pages/categories";
import CategoryDetails from "pages/category-details";
import ProductTypesPage from "pages/ProductTypes";
import Supplements from "pages/supplements";
import TrendingSupplements from "pages/trending-supplements";
import BrandDetailsPage from "pages/brands-details";
import SupplementDetailsPage from "pages/supplement-details";
import TreningSupplementDetailsPage from "pages/trending-supplement-details";
import AddBrandPage from "pages/add-brand";
import AddSupplementPage from "pages/add-supplement";
import TopRequestedPage from "pages/top-requested";
import TestRequestsPage from "pages/test-requests";
import PaidTestRequestsPage from "pages/paid-test-requests";
import BannersPage from "pages/banners";
import ManagePotencyPage from "pages/manage-potency";
import AppUpdates from "pages/app-updates";
import ManageNotificationsPage from "pages/manage-notifications";
import PromoCodesPage from "pages/promo-codes";
import PromoBannersPage from "pages/promo-banners";
import VideoBannersPage from "pages/video-banners";

function AppRouter() {
  const { isAuthenticated, user } = useAuth();

  const withPermissions = (route: string, element: JSX.Element) => {
    const permissions = ROUTE_PERMISSIONS[route];

    if (!permissions?.length) {
      return element;
    }

    return <RequireAuth permissions={permissions}>{element}</RequireAuth>;
  };

  return (
    <Routes>
      {/* dashboard routes */}
      <Route element={<PageLayout />}>
        <Route
          path={ROUTES.DASHBOARD}
          element={withPermissions(ROUTES.DASHBOARD, <Dashboard />)}
        />
        <Route
          path={ROUTES.ADMIN_USERS}
          element={withPermissions(ROUTES.ADMIN_USERS, <AdminUsers />)}
        />
        <Route
          path={ROUTES.USERS}
          element={withPermissions(ROUTES.USERS, <Users />)}
        />
        <Route
          path={ROUTES.USER_DETAILS}
          element={withPermissions(ROUTES.USER_DETAILS, <UserDetails />)}
        />
        <Route
          path={ROUTES.SCANS}
          element={withPermissions(ROUTES.SCANS, <Scans />)}
        />
        <Route
          path={ROUTES.BRANDS}
          element={withPermissions(ROUTES.BRANDS, <Brands />)}
        />
        <Route
          path={ROUTES.ADD_BRAND}
          element={withPermissions(ROUTES.ADD_BRAND, <AddBrandPage />)}
        />
        <Route
          path={ROUTES.BRAND_DETAILS}
          element={withPermissions(ROUTES.BRAND_DETAILS, <BrandDetailsPage />)}
        />
        <Route
          path={ROUTES.CATEGORIES}
          element={withPermissions(ROUTES.CATEGORIES, <CategoriesPage />)}
        />
        <Route
          path={ROUTES.CATEGORY_DETAILS}
          element={withPermissions(ROUTES.CATEGORY_DETAILS, <CategoryDetails />)}
        />
        <Route
          path={ROUTES.PRODUCT_TYPES}
          element={withPermissions(ROUTES.PRODUCT_TYPES, <ProductTypesPage />)}
        />
        <Route
          path={ROUTES.SUPPLEMENTS}
          element={withPermissions(ROUTES.SUPPLEMENTS, <Supplements />)}
        />
        <Route
          path={ROUTES.PROMO_CODES}
          element={withPermissions(ROUTES.PROMO_CODES, <PromoCodesPage />)}
        />
        <Route
          path={ROUTES.PROMO_BANNERS}
          element={withPermissions(ROUTES.PROMO_BANNERS, <PromoBannersPage />)}
        />
        <Route
          path={ROUTES.TRENDING_SUPPLEMENTS}
          element={withPermissions(
            ROUTES.TRENDING_SUPPLEMENTS,
            <TrendingSupplements />
          )}
        />
        <Route
          path={ROUTES.ADD_SUPPLEMENT}
          element={withPermissions(ROUTES.ADD_SUPPLEMENT, <AddSupplementPage />)}
        />
        <Route
          path={ROUTES.TOP_REQUESTED_SUPPLEMENTS}
          element={withPermissions(
            ROUTES.TOP_REQUESTED_SUPPLEMENTS,
            <TopRequestedPage />
          )}
        />
        <Route
          path={ROUTES.TEST_REQUESTS}
          element={withPermissions(ROUTES.TEST_REQUESTS, <TestRequestsPage />)}
        />
        <Route
          path={ROUTES.PAID_TEST_REQUESTS}
          element={withPermissions(
            ROUTES.PAID_TEST_REQUESTS,
            <PaidTestRequestsPage />
          )}
        />
        <Route
          path={ROUTES.BANNERS}
          element={withPermissions(ROUTES.BANNERS, <BannersPage />)}
        />
        <Route
          path={ROUTES.VIDEO_BANNERS}
          element={withPermissions(ROUTES.VIDEO_BANNERS, <VideoBannersPage />)}
        />
        <Route
          path={ROUTES.MANAGE_NOTIFICATIONS}
          element={withPermissions(
            ROUTES.MANAGE_NOTIFICATIONS,
            <ManageNotificationsPage />
          )}
        />
        <Route
          path={ROUTES.MANAGE_POTENCY}
          element={withPermissions(ROUTES.MANAGE_POTENCY, <ManagePotencyPage />)}
        />
        <Route
          path={ROUTES.SUPPLEMENT_DETAILS}
          element={withPermissions(
            ROUTES.SUPPLEMENT_DETAILS,
            <SupplementDetailsPage />
          )}
        />
        <Route
          path={ROUTES.TRENDING_SUPPLEMENT_DETAILS}
          element={withPermissions(
            ROUTES.TRENDING_SUPPLEMENT_DETAILS,
            <TreningSupplementDetailsPage />
          )}
        />
      </Route>
      {/* authentication routes */}
      <Route element={<AuthLayout />}>
        <Route path={ROUTES.LOGIN} element={<Login />} />
      </Route>
      <Route path={ROUTES.APP_UPDATES} element={<AppUpdates />} />
      {/* common routes */}
      <Route path="/404" element={<Page404 />} />
      <Route path="*" element={<Navigate replace to="/404" />} />
      <Route
        index
        element={
          isAuthenticated ? (
            <Navigate to={getDefaultAuthorizedRoute(user)} replace />
          ) : (
            <Navigate to={ROUTES.LOGIN} replace />
          )
        }
      />
    </Routes>
  );
}

export default AppRouter;
